import {matchPath} from 'react-router-dom';

import {routeUrls} from './routeUrls';

/**
 * Альфа-прод (SKS-341): первый боевой контур, открытый только фулфилменту.
 *
 * Регистрации селлера на нём нет — тип «Продавец / клиент» в мастере компании выключен
 * (`companyTypeSelection.ts`). Фулфилменту открыта его работа целиком, кроме разделов,
 * перечисленных у `ALLOWED_ROUTES` как закрытые.
 *
 * Закрытое при этом остаётся на экране выключенным, а не исчезает: спрятанный раздел
 * человек не отличает от несуществующего — он решает, что такого в сервисе нет, и идёт
 * искать это в другом месте. Выключенный говорит «будет позже». Отсюда две вещи:
 * `ALLOWED_ROUTES` ниже и общая подсказка `ALPHA_PROD_DISABLED_HINT`.
 *
 * Список адресов — именно белый, а не чёрный. Разделов в кабинете полсотни, и новый,
 * добавленный после этой правки, обязан быть закрыт по умолчанию: забытый пункт в белом
 * списке — это недоступный экран на стенде, забытый в чёрном — открытая недоделка у клиента.
 */

/**
 * Ключ в localStorage, которым режим включается вручную.
 *
 * Нужен для стейджа: тестировщик проверяет альфа-прод в своём браузере, не выключая
 * остальным разделы, которые они в это же время тестируют (флаг контура сделал бы стенд
 * непригодным для всех сразу). Значение читается как строка `'true'`.
 */
const ALPHA_PROD_KEY = 'alpha_prod';

function readLocalOverride(): boolean {
	try {
		return window.localStorage.getItem(ALPHA_PROD_KEY) === 'true';
	} catch {
		/*
		 * Приватный режим и запрещённые куки роняют сам доступ к хранилищу. Отсутствие
		 * переключателя — это «переключателя нет», а не повод уронить кабинет.
		 */
		return false;
	}
}

/**
 * Включён ли режим. Снимок на загрузку страницы, а не чтение на каждый рендер.
 *
 * Меню, маршруты и анкета склада спрашивают флаг в разных местах и в разное время;
 * значение, меняющееся посреди сессии, дало бы кабинет, наполовину открытый и наполовину
 * закрытый, — состояние, которого нет ни на проде, ни на стейдже. Переключили ключ —
 * перезагрузите страницу.
 *
 * Переменная окружения только включает: `VITE_ALPHA_PROD` вшивается в бандл на сборке
 * (см. `DOTENV_WEB_PROD`), и localStorage её не отменяет. Иначе режим на боевом контуре
 * снимался бы строчкой в консоли браузера.
 */
export const ALPHA_PROD: boolean =
	import.meta.env.VITE_ALPHA_PROD === 'true' || readLocalOverride();

/**
 * Адреса, которые в режиме альфа-прода остаются открытыми.
 *
 * Фулфилменту открыты настройка склада (структура, тара, тарифы, рабочие места),
 * сотрудники компании и склада с их журналом, принтеры, транспорт, отзывы, поддержка,
 * чаты, каталог складов сети («Сеть» — у фулфилмента в ней только каталог, SKS-636),
 * заказы, входящие поставки, «Финансы», «Хранение» и складской поток в «Операциях»:
 * приёмка, размещение, сборка, упаковка, сортировка, отгрузка и рейсы.
 *
 * Закрыты решением о составе первого релиза (02.11.2026) товарные разделы
 * «Операций» — задания от клиентов, отмены, возвраты, споры — и «Обучение».
 *
 * Из поставок открыта только карточка: список, создание и черновик — селлерские,
 * а регистрации селлера в этом релизе нет.
 *
 * По этому же списку меню помечает свои пункты выключенными (`lockClosedSections`
 * в `navModel.ts`): второго перечня разделов в проекте нет и быть не должно.
 */
const ALLOWED_ROUTES: string[] = [
	routeUrls.ROOT,
	routeUrls.PUBLISHED_WAREHOUSE_OFFER,
	routeUrls.PROFILE,
	routeUrls.COMPANIES.LIST,
	routeUrls.COMPANIES.NEW,
	routeUrls.COMPANIES.CARD,
	routeUrls.COMPANIES.OFFER,
	routeUrls.COMPANIES.MEMBERS,
	routeUrls.COMPANIES.WAREHOUSE_LIST,
	routeUrls.COMPANIES.WAREHOUSE_NEW,
	routeUrls.COMPANIES.WAREHOUSE_CARD,
	routeUrls.COMPANIES.WAREHOUSE_TOPOLOGY,
	routeUrls.COMPANIES.WAREHOUSE_TOPOLOGY_ZONE,
	routeUrls.COMPANIES.WAREHOUSE_TOPOLOGY_ZONE_ADDRESSES,
	routeUrls.COMPANIES.WAREHOUSE_TOPOLOGY_UNIT,
	routeUrls.COMPANIES.WAREHOUSE_CONTAINERS,
	routeUrls.COMPANIES.WAREHOUSE_TARIFFS,
	routeUrls.COMPANIES.WAREHOUSE_EMPLOYEES,
	routeUrls.COMPANIES.WAREHOUSE_STAFF_ACTIVITY,
	routeUrls.COMPANIES.WAREHOUSE_WORKPLACES,
	routeUrls.COMPANIES.WAREHOUSE_WORKPLACE_OPERATIONS,
	routeUrls.COMPANIES.WAREHOUSE_PRINTERS,
	routeUrls.COMPANIES.WAREHOUSE_VEHICLES,
	routeUrls.COMPANIES.WAREHOUSE_REVIEWS,
	routeUrls.COMPANIES.CHAT_LIST,
	routeUrls.COMPANIES.CHAT_CARD,
	routeUrls.COMPANIES.SUPPORT_LIST,
	routeUrls.COMPANIES.SUPPORT_CARD,
	routeUrls.COMPANIES.CATALOG_LIST,
	routeUrls.COMPANIES.CATALOG_COMPARE,
	routeUrls.COMPANIES.CATALOG_CARD,
	routeUrls.COMPANIES.ORDER_LIST,
	routeUrls.COMPANIES.ORDER_CARD,
	routeUrls.COMPANIES.INCOMING_SUPPLY_LIST,
	routeUrls.COMPANIES.SUPPLY_CARD,
	routeUrls.COMPANIES.FINANCE_BALANCE,
	routeUrls.COMPANIES.FINANCE_OPERATIONS,
	routeUrls.COMPANIES.FINANCE_PAYOUTS,
	routeUrls.COMPANIES.FINANCE_ACCRUALS,
	routeUrls.COMPANIES.FINANCE_HOLDS,
	routeUrls.COMPANIES.STORAGE,
	routeUrls.COMPANIES.INVENTORY_LIST,
	routeUrls.COMPANIES.INVENTORY_LIST_WAREHOUSE,
	routeUrls.COMPANIES.INVENTORY_ITEM,
	routeUrls.COMPANIES.INVENTORY_LOTS,
	routeUrls.COMPANIES.RECEIVING_QUEUE,
	routeUrls.COMPANIES.RECEIVING_QUEUE_WAREHOUSE,
	routeUrls.COMPANIES.PLACEMENT_QUEUE,
	routeUrls.COMPANIES.PLACEMENT_QUEUE_WAREHOUSE,
	routeUrls.COMPANIES.PLACEMENT_SUPPLY,
	routeUrls.COMPANIES.PICKING_QUEUE,
	routeUrls.COMPANIES.PICKING_QUEUE_WAREHOUSE,
	routeUrls.COMPANIES.PICKING_WAVES,
	routeUrls.COMPANIES.PICKING_WAVES_WAREHOUSE,
	routeUrls.COMPANIES.PACKING_QUEUE,
	routeUrls.COMPANIES.PACKING_QUEUE_WAREHOUSE,
	routeUrls.COMPANIES.PACKING_TERMINAL,
	routeUrls.COMPANIES.SORTING_QUEUE,
	routeUrls.COMPANIES.SORTING_QUEUE_WAREHOUSE,
	routeUrls.COMPANIES.DISPATCH_QUEUE,
	routeUrls.COMPANIES.DISPATCH_QUEUE_WAREHOUSE,
	routeUrls.COMPANIES.TRIPS,
	routeUrls.COMPANIES.TRIPS_WAREHOUSE,
	routeUrls.COMPANIES.TRIP_CARD,
];

/**
 * Адреса, которые совпадают с открытым шаблоном, но ведут на закрытый экран.
 *
 * `/supplies/new` подходит под карточку поставки `/supplies/:supplyId` — «new» читается
 * как идентификатор, и без этой строки форма создания поставки открывалась бы, хотя
 * создание поставок — селлерское и закрыто.
 */
const CLOSED_ROUTES: string[] = [routeUrls.COMPANIES.SUPPLY_NEW];

/** Открыт ли адрес в режиме альфа-прода. Вне режима открыто всё. */
export function isAlphaProdRouteAllowed(pathname: string): boolean {
	return (
		ALLOWED_ROUTES.some((pattern) => matchPath(pattern, pathname) !== null) &&
		!CLOSED_ROUTES.some((pattern) => matchPath(pattern, pathname) !== null)
	);
}

/**
 * Подсказка у выключенного элемента — одна на весь кабинет.
 *
 * Закрытое показывается, а не прячется: человек видит, что в сервисе есть, и знает,
 * что этого пока нет, а не считает, что раздела не существует. Но выключенный пункт
 * без объяснения читается как поломка, поэтому текст один и тот же везде — в меню,
 * на кнопках, на карточках выбора и над выключенными блоками. Ни «раздел», ни «склады»
 * в нём не названы: та же подсказка висит и на типе компании, и на кнопке входа.
 */
export const ALPHA_PROD_DISABLED_HINT = 'Данный функционал находится в разработке';
